<template>
  <div class="modal fade" id="semesterFormModal" tabindex="-1" aria-labelledby="semesterFormModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="semesterFormModalLabel">{{ isEdit ? 'Edit semester' : 'Create semester' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitForm">
            <div class="mb-3">
              <label for="semesterName" class="form-label">Semester name</label>
              <input type="text" id="semesterName" class="form-control" v-model.trim="name" placeholder="Spring 2026" required>
            </div>
            <div class="mb-3">
              <label for="semesterStart" class="form-label">Start date</label>
              <div class="input-group position-relative">
                <input type="text" id="semesterStart" class="form-control" :value="startText"
                  @input="onStartInput($event.target.value)" placeholder="DD.MM.YYYY" inputmode="numeric" autocomplete="off" required>
                <button type="button" class="btn btn-outline-secondary" @click="openPicker('start')"
                  aria-label="Open calendar" title="Open calendar"><i class="bi bi-calendar-event"></i></button>
                <input type="date" ref="startPicker" class="semester-native-picker" tabindex="-1" aria-hidden="true"
                  :value="start_date" @change="onStartPick($event.target.value)">
              </div>
              <div class="form-text text-danger" v-if="startText && !start_date">Enter a valid date (DD.MM.YYYY).</div>
            </div>
            <div class="mb-3">
              <label for="semesterEnd" class="form-label">End date</label>
              <div class="input-group position-relative">
                <input type="text" id="semesterEnd" class="form-control" :value="endText"
                  @input="onEndInput($event.target.value)" placeholder="DD.MM.YYYY" inputmode="numeric" autocomplete="off" required>
                <button type="button" class="btn btn-outline-secondary" @click="openPicker('end')"
                  aria-label="Open calendar" title="Open calendar"><i class="bi bi-calendar-event"></i></button>
                <input type="date" ref="endPicker" class="semester-native-picker" tabindex="-1" aria-hidden="true"
                  :value="end_date" @change="onEndPick($event.target.value)">
              </div>
              <div class="form-text text-danger" v-if="endText && !end_date">Enter a valid date (DD.MM.YYYY).</div>
              <div class="form-text text-danger" v-if="!isDateOrderValid">End date must be after start date.</div>
            </div>
            <!-- Copy is only offered on create; editing never copies resources. -->
            <template v-if="!isEdit">
              <hr>
              <div class="mb-3 form-check">
                <input type="checkbox" id="copyResourcesCreate" class="form-check-input" v-model="copy_resources_from_last_semester">
                <label for="copyResourcesCreate" class="form-check-label">
                  Copy global files, homeworks, and subtasks from the current semester
                </label>
                <div class="form-text">
                  If enabled, reusable course resources are copied into the new semester. This can take a while.
                </div>
              </div>
            </template>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="submitting">Close</button>
              <button type="submit" class="btn btn-primary" :disabled="!canSubmit || submitting">
                <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ isEdit ? 'Save' : 'Create' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>

  <StemErrorModal ref="stemErrorModal" />
</template>

<script>
import { Modal } from 'bootstrap';
import StemErrorModal from '@/components/StemErrorModal.vue';
import Semester from '@/models/semester';

export default {
  name: 'SemesterFormModal',
  components: { StemErrorModal },
  data() {
    return {
      id: null,
      name: '',
      // *_date hold the canonical ISO "YYYY-MM-DD" (wire format); *Text hold the
      // "D.M.YYYY" display strings the user sees/types, matching the table.
      start_date: '',
      end_date: '',
      startText: '',
      endText: '',
      copy_resources_from_last_semester: false,
      submitting: false,
    };
  },
  computed: {
    isEdit() {
      return this.id !== null;
    },
    isDateOrderValid() {
      if (!this.start_date || !this.end_date) return true;
      const start = new Date(this.start_date);
      const end = new Date(this.end_date);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return true;
      return start < end;
    },
    canSubmit() {
      return !!this.name && !!this.start_date && !!this.end_date && this.isDateOrderValid;
    },
  },
  methods: {
    // semester === null => create mode; a semester object => edit mode (prefilled).
    show(semester = null) {
      if (semester) {
        this.id = semester.id;
        this.name = semester.name;
        // Prefill from the display format, then derive ISO from the same text so
        // the two stay consistent (and timezone-safe for a naive backend datetime).
        this.onStartInput(Semester.formatDate(semester.start_date));
        this.onEndInput(Semester.formatDate(semester.end_date));
        this.copy_resources_from_last_semester = false;
        this.submitting = false;
      } else {
        this.resetForm();
      }
      const el = document.getElementById('semesterFormModal');
      const instance = Modal.getInstance(el) || new Modal(el);
      instance.show();
    },
    hide() {
      const el = document.getElementById('semesterFormModal');
      const instance = Modal.getInstance(el);
      if (instance) instance.hide();
    },
    onStartInput(value) {
      this.startText = value;
      this.start_date = Semester.parseDisplayDate(value);
    },
    onEndInput(value) {
      this.endText = value;
      this.end_date = Semester.parseDisplayDate(value);
    },
    // Opens the native OS date picker anchored to the calendar button. The picker
    // is only an affordance — the D.M.YYYY text field stays the source of truth
    // (so the modal keeps matching the table, unlike a bare <input type="date">).
    openPicker(which) {
      const el = which === 'start' ? this.$refs.startPicker : this.$refs.endPicker;
      if (el && typeof el.showPicker === 'function') {
        try {
          el.showPicker();
        } catch (e) {
          // No transient user activation / unsupported context — silently ignore.
        }
      }
    },
    // iso is the native "YYYY-MM-DD"; route through the text path so *_date and
    // *Text stay derived from one D.M.YYYY string (and avoid timezone-shift parsing).
    onStartPick(iso) {
      if (!iso) return;
      const [y, m, d] = iso.split('-').map(Number);
      this.onStartInput(`${d}.${m}.${y}`);
    },
    onEndPick(iso) {
      if (!iso) return;
      const [y, m, d] = iso.split('-').map(Number);
      this.onEndInput(`${d}.${m}.${y}`);
    },
    resetForm() {
      this.id = null;
      this.name = '';
      this.start_date = '';
      this.end_date = '';
      this.startText = '';
      this.endText = '';
      this.copy_resources_from_last_semester = false;
      this.submitting = false;
    },
    submitForm() {
      if (!this.canSubmit) {
        this.$refs.stemErrorModal.show('Invalid input', 'Please fill all fields; start date must be before end date.');
        return;
      }
      this.$emit('submitForm', {
        id: this.id,
        name: this.name,
        start_date: this.start_date,
        end_date: this.end_date,
        // Backend ignores this on update; keep it a boolean so Semester.validate passes.
        copy_resources_from_last_semester: this.isEdit ? false : this.copy_resources_from_last_semester,
        archived: false,
      });
      this.hide();
    },
  },
};
</script>

<style scoped>
/* The native date input is a picker affordance only: visually collapsed under the
   calendar button so its popup anchors there. .input-group is position-relative. */
.semester-native-picker {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  opacity: 0;
  pointer-events: none;
}
</style>
